import React, { useState } from 'react';
import { Task } from '../types';
import { Sparkles, Plus, Trash2, Clock, CheckCircle } from 'lucide-react';
import { persianNumber, PERSIAN_WEEK_DAYS } from '../services/utils';
import { generateScheduleWithAI } from '../services/geminiService';

interface StudySchedulerProps {
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  daysUntilDeadline: number;
}

const StudyScheduler: React.FC<StudySchedulerProps> = ({ tasks, setTasks, daysUntilDeadline }) => {
  const [loading, setLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [generatedSchedule, setGeneratedSchedule] = useState<any[]>([]);

  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    subject: '',
    durationMinutes: 60,
    priority: 'Medium'
  });

  const addTask = () => {
    if (!newTask.title || !newTask.subject) return;
    const task: Task = {
      id: Date.now().toString(),
      title: newTask.title,
      subject: newTask.subject,
      durationMinutes: newTask.durationMinutes || 60,
      priority: newTask.priority as any,
      completed: false
    };
    setTasks([...tasks, task]);
    setNewTask({ title: '', subject: '', durationMinutes: 60, priority: 'Medium' });
  };

  const removeTask = (id: string) => {
    setTasks(tasks.filter(t => t.id !== id));
  };

  const handleGenerateSchedule = async () => {
    if (tasks.length === 0) return;
    
    setLoading(true);
    setAiMessage(null);
    
    try {
      // Assuming 4 hours/day availability as default
      const { schedule, message } = await generateScheduleWithAI(tasks, 4, daysUntilDeadline);
      
      setGeneratedSchedule(schedule);
      setAiMessage(message);
    } catch (error) {
      console.error("AI Generation Error", error);
      setAiMessage("خطا در ارتباط با هوش مصنوعی. لطفا اتصال اینترنت را بررسی کنید.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-auto lg:h-full">
      {/* Left: Task Input & List */}
      <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-4 md:p-6 flex flex-col h-[500px] lg:h-full">
        <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">وظایف تحصیلی</h2>
        
        {/* Input Form */}
        <div className="space-y-3 mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
          <input 
            type="text" 
            placeholder="عنوان (مثلاً: خواندن فصل ۴)" 
            className="w-full p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:border-primary-500"
            value={newTask.title}
            onChange={e => setNewTask({...newTask, title: e.target.value})}
          />
          <input 
            type="text" 
            placeholder="موضوع (مثلاً: فیزیک)" 
            className="w-full p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:border-primary-500"
            value={newTask.subject}
            onChange={e => setNewTask({...newTask, subject: e.target.value})}
          />
          <div className="flex gap-2">
            <select 
              className="p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm flex-1"
              value={newTask.priority}
              onChange={e => setNewTask({...newTask, priority: e.target.value as any})}
            >
              <option value="High">بالا</option>
              <option value="Medium">متوسط</option>
              <option value="Low">پایین</option>
            </select>
            <input 
              type="number" 
              className="w-20 p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
              placeholder="دقیقه"
              value={newTask.durationMinutes}
              onChange={e => setNewTask({...newTask, durationMinutes: parseInt(e.target.value)})}
            />
          </div>
          <button 
            onClick={addTask}
            className="w-full bg-slate-800 dark:bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={16} /> افزودن
          </button>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto space-y-2 pl-2 custom-scrollbar">
          {tasks.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-10">
              هنوز وظیفه‌ای اضافه نشده است.
            </div>
          )}
          {tasks.map(task => (
            <div key={task.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-lg hover:shadow-sm transition-shadow group">
              <div>
                <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm">{task.title}</h4>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold
                    ${task.priority === 'High' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 
                      task.priority === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' : 
                      'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'}`}>
                    {task.priority === 'High' ? 'بالا' : task.priority === 'Medium' ? 'متوسط' : 'پایین'}
                  </span>
                  <span>{task.subject}</span>
                  <span>• {persianNumber(task.durationMinutes)} دقیقه</span>
                </div>
              </div>
              <button 
                onClick={() => removeTask(task.id)}
                className="text-slate-300 hover:text-red-500 transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Scheduler Display */}
      <div className="lg:col-span-2 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-slate-900 dark:to-indigo-950 rounded-2xl p-4 md:p-6 flex flex-col min-h-[500px] lg:h-full border border-blue-100 dark:border-slate-800 relative overflow-hidden">
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 z-10">
          <div>
             <h2 className="text-xl md:text-2xl font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
              <Sparkles className="text-indigo-500" />
              برنامه‌ریز هوشمند
            </h2>
            <p className="text-indigo-600 dark:text-indigo-300 text-xs md:text-sm mt-1">
              بر اساس اولویت وظایف، برنامه مطالعه خود را دریافت کنید.
            </p>
          </div>
          <button 
            onClick={handleGenerateSchedule}
            disabled={loading || tasks.length === 0}
            className={`
              w-full sm:w-auto px-6 py-2.5 rounded-xl font-semibold shadow-lg transition-all text-sm
              ${loading || tasks.length === 0 
                ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                : 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white hover:bg-indigo-50 dark:hover:bg-indigo-700 hover:scale-105'}
            `}
          >
            {loading ? 'در حال تفکر...' : 'ایجاد برنامه با هوش مصنوعی'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto z-10 custom-scrollbar space-y-4">
          {aiMessage && (
            <div className="bg-white/60 dark:bg-slate-800/80 p-4 rounded-xl text-indigo-800 dark:text-indigo-200 text-sm italic mb-4 border border-white dark:border-slate-700 animate-in fade-in slide-in-from-top-2">
              "{aiMessage}"
            </div>
          )}

          {generatedSchedule.length > 0 ? (
            generatedSchedule.map((dayPlan, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-sm border border-indigo-100/50 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2" style={{animationDelay: `${idx * 100}ms`}}>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm md:text-base">{dayPlan.day}</h3>
                  <span className="text-[10px] md:text-xs bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full font-medium">
                    تمرکز: {dayPlan.focus}
                  </span>
                </div>
                <div className="space-y-2">
                  {dayPlan.tasks?.map((t: string | any, tIdx: number) => (
                    <div key={tIdx} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400 p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0"></div>
                      {/* Safety check to ensure we render a string */}
                      <span className="line-clamp-2">{typeof t === 'string' ? t : JSON.stringify(t)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-indigo-300 dark:text-indigo-500/50">
               <Clock size={48} className={`mb-4 ${loading ? 'animate-spin' : 'opacity-50'}`} />
               <p className="text-sm text-center">
                 {loading ? 'هوش مصنوعی در حال برنامه‌ریزی است...' : 'وظایف را اضافه کنید و دکمه ایجاد را بزنید'}
               </p>
            </div>
          )}
        </div>

        {/* Background Decorations */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-200 dark:bg-indigo-600 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-200 dark:bg-blue-600 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 translate-x-1/2"></div>
      </div>
    </div>
  );
};

export default StudyScheduler;